import React, { useEffect, useCallback, useState } from "react";
import Grid from "@carrier-io/air-react/Grid";
import Typography from "@carrier-io/air-react/Typography";
import { useNavigate, useSearchParams } from "react-router-dom";
import { EmailLookupResponse } from "../../generated/graphql";
import { AuthMethodsMapper, RoutesPage } from "../../constants/routes";
import { EmailLookupMutation } from "../../graphql/mutations/emailLookup";
import { Clients, useMutation } from "../../apollo";
import { ControllableInput } from "../../components/controllable/Input";
import { useForm } from "react-hook-form";
import { EmailInput } from "../../validations/email";
import { classValidatorResolver } from "@hookform/resolvers/class-validator";
import { SubmitButton } from "../../components/submitButton/SubmitButton";
import Box from "@carrier-io/air-react/Box";
import { useAlerts } from "../../contexts/alert/AlertsContext";
import { AlertTypes } from "../../contexts/alert/types";
import { displayError } from "../../constants/error";
import {
  getRefreshToken,
  getUserLookupData,
  setUserLookupsCookies as setUserLookupsCookies,
} from "../../utils/cookie";
import { useGetCurrentUser } from "../../hooks/useGetCurrentUser";
import { Alert } from "@carrier-io/air-react";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { useClearApolloCache } from "../../hooks/useClearApolloCache";

export const EmailLoginPage = () => {
  const [searchParams] = useSearchParams();
  const { pushAlert } = useAlerts();
  const redirectTo = searchParams.get("redirect_to");
  const refreshToken = getRefreshToken();
  const loggedInUser = useGetCurrentUser();
  const clearApolloCache = useClearApolloCache();
  const prevError = searchParams.get("prevError");
  const [isRedirecting, setIsRedirecting] = useState(false);

  const {
    email = "",
    username = "",
    authMethod = "",
    region = "",
  } = getUserLookupData();
  const navigate = useNavigate();
  const resolver = classValidatorResolver<EmailInput>(EmailInput);
  const {
    control,
    handleSubmit,
    clearErrors,
    formState: { errors, isValid },
  } = useForm<EmailInput>({
    mode: "onBlur",
    resolver,
    defaultValues: {
      email: "",
    },
  });

  const [runEmailLookup, emailLookupResult] = useMutation<
    { emailLookup: EmailLookupResponse },
    {
      email: string;
      origin: string;
    }
  >(Clients.Authn, EmailLookupMutation);

  const handleSubmitEmail = async (data: EmailInput) => {
    const { email } = data;

    try {
      await runEmailLookup({
        variables: { email, origin: window.location.origin },
      });
    } catch (error) {
      pushAlert(AlertTypes.ERROR, displayError(error));
    }
  };

  const handleRedirect = useCallback(
    (redirectTo: string) => {
      if (!redirectTo) {
        navigate({ pathname: RoutesPage.Dashboard });
        return;
      }

      const isExternal = /^https?:\/\//i.test(redirectTo);

      if (isExternal) {
        window.location.href = redirectTo;
      } else {
        navigate({ pathname: redirectTo });
      }
    },
    [navigate],
  );

  useEffect(() => {
    clearApolloCache();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (loggedInUser) {
      handleRedirect(redirectTo ?? "");
    }
  }, [loggedInUser, redirectTo, navigate, handleRedirect]);

  useEffect(() => {
    if (email && username && authMethod && !refreshToken) {
      if (authMethod === "CREDENTIALS") {
        const params = new URLSearchParams(searchParams);
        params.set("redirect_to", redirectTo || "");
        params.set("email", email);
        params.set("username", username);
        params.set("region", region);

        navigate({
          pathname:
            AuthMethodsMapper[authMethod as keyof typeof AuthMethodsMapper],
          search: `?${params.toString()}`,
        });
      }
    }
  }, [
    email,
    username,
    authMethod,
    region,
    navigate,
    searchParams,
    redirectTo,
    refreshToken,
  ]);

  useEffect(() => {
    if (emailLookupResult.data || emailLookupResult.error) {
      const authMethod = emailLookupResult.data?.emailLookup.method || "";
      const username = emailLookupResult.data?.emailLookup.username ?? "";
      const email = emailLookupResult.data?.emailLookup.email ?? "";
      const region = emailLookupResult.data?.emailLookup.region ?? "";
      const url =
        emailLookupResult.data?.emailLookup.methodURL ?? window.location.origin;
      let isInternalRedirect = false;

      try {
        isInternalRedirect = new URL(url).origin === window.location.origin;
      } catch (error) {
        console.error("Invalid URL:", url);
        // Fallback to treating it as external redirect
        isInternalRedirect = false;
      }

      setUserLookupsCookies(
        {
          username,
          email,
          region,
          authMethod,
          redirectTo: redirectTo || "",
        },
        window.location.hostname,
      );

      if (isInternalRedirect) {
        const params = new URLSearchParams(searchParams);
        params.set("redirect_to", redirectTo || "");
        params.set("email", email);
        params.set("username", username);
        params.set("region", region);

        const pathname =
          AuthMethodsMapper[authMethod as keyof typeof AuthMethodsMapper];

        navigate({
          pathname,
          search: `?${params.toString()}`,
        });
      } else {
        setIsRedirecting(true);
        window.location.href = url;
      }
    }
  }, [
    emailLookupResult.data,
    emailLookupResult.error,
    redirectTo,
    navigate,
    searchParams,
  ]);

  return (
    <>
      <Grid>
        <Typography variant="body2">Sign in with your email address</Typography>
      </Grid>
      {prevError && (
        <Alert
          severity="error"
          icon={
            <WarningAmberIcon
              sx={{
                alignSelf: "center",
              }}
            />
          }
          sx={{
            backgroundColor: "#fff6f6",
            color: "black",
            borderRadius: "8px",
            boxShadow: "0px 3px 5px 0px rgba(20, 22, 24, 0.2)",
            maxWidth: 500,
            display: "flex",
            alignItems: "center",
          }}
        >
          {prevError}
        </Alert>
      )}
      <Box maxWidth="338px" width="100%">
        <form onSubmit={handleSubmit(handleSubmitEmail)}>
          <Grid height="76px" marginTop="25px">
            <ControllableInput
              control={control}
              error={errors.email?.message}
              type="email"
              name="email"
              color="primary"
              size="large"
              label="Email Address"
              hideBackgroundColor
              fullWidth
              showBorder
              clearErrors={clearErrors}
              hideLabel={false}
            />
          </Grid>
          <Grid>
            <SubmitButton
              isLoading={emailLookupResult.loading}
              disabled={!isValid || emailLookupResult.loading || isRedirecting}
            >
              Next
            </SubmitButton>
          </Grid>
        </form>
      </Box>
    </>
  );
};
